import {createFeatureSelector, createSelector, MemoizedSelector} from '@ngrx/store';
import {IStore, isStoreOperationallyDisabled} from '../../../shared/shared-models/store/store';
import {IUserStoresState} from './user-store.reducer';
import {Features, IFeature, INavigate} from '../models';
import {NavigationPageEnum} from '../models/navigations.enums';
import {IUserAccessState} from '../user-access-store/user-access.reducer';
import {ISettingState} from '../../settings-new/store/setting.reducer';
import {StoreDisplayOrderEnum, TStoreDisplayOrder} from '../../settings-new/models';

/**
 * Selector for accessing the 'user-stores' feature state.
 *
 * @template MemoizedSelector
 * @returns {MemoizedSelector<object, IUserStoresState>} The user store state.
 */
export const selectSharedUserStoreState = createFeatureSelector<IUserStoresState>('user-stores');

/**
 * Selector for retrieving the selected feature.
 *
 * @template MemoizedSelector
 * @returns {MemoizedSelector<object, IFeature>} The selected feature object.
 */
export const selectSelectedFeature = createSelector(
  selectSharedUserStoreState,
  (state: IUserStoresState) => state.selectedFeature || Features[NavigationPageEnum.Home],
);

export const selectUserAccessStateInUserStores = createFeatureSelector<IUserAccessState>('user-access');

export const selectSettingsStateInUserStores = createFeatureSelector<ISettingState>('setting');

/**
 * Selector for retrieving stores by feature directly from user-access state.
 * This avoids circular dependency with user-access.selectors.ts.
 */
export const selectLocalStoresByFeature = createSelector(
  selectUserAccessStateInUserStores,
  (state: IUserAccessState): Record<string, IStore[]> => state.userAccessPerFeature || {},
);

/**
 * All stores loaded for the user (including DISABLED), from user-stores state.
 */
export const selectAllUserStores = createSelector(
  selectSharedUserStoreState,
  (state: IUserStoresState): IStore[] => state.stores || [],
);

/**
 * Selector for retrieving the store display order preference.
 */
export const selectLocalStoreDisplayOrder = createSelector(
  selectSettingsStateInUserStores,
  (state: ISettingState): TStoreDisplayOrder => state?.application?.preferences?.storeDisplayOrder ?? 'Alphabetical',
);

/**
 * Selector for retrieving the custom store order array.
 */
export const selectLocalCustomStoreOrder = createSelector(
  selectSettingsStateInUserStores,
  (state: ISettingState): string[] => state?.application?.preferences?.customStoreOrder ?? [],
);

/**
 * Selector for retrieving raw stores by feature without any ordering applied.
 * Use this when the component will handle ordering itself.
 */
export const selectStoresRaw = createSelector(
  selectLocalStoresByFeature,
  selectSelectedFeature,
  (stores: Record<string, IStore[]>, feature: IFeature): IStore[] => stores[feature.feature] || <IStore[]>[],
);

/**
 * Orders active stores first, then DISABLED stores (greyed-out in the selector).
 */
const orderActiveStoresThenDisabled = (stores: IStore[]): IStore[] => {
  const active: IStore[] = [];
  const disabled: IStore[] = [];
  stores.forEach((store: IStore) => {
    if (isStoreOperationallyDisabled(store)) {
      disabled.push(store);
    } else {
      active.push(store);
    }
  });
  return [...active, ...disabled];
};

/**
 * Merges feature-accessible stores with DISABLED stores the user still has assigned,
 * so the header dropdown can show disabled stores as greyed-out options.
 */
const mergeFeatureStoresWithDisabled = (featureStores: IStore[], allUserStores: IStore[]): IStore[] => {
  const merged: IStore[] = [...featureStores];
  const seenIds = new Set(featureStores.map((store: IStore) => store.storeId));
  allUserStores.forEach((store: IStore) => {
    if (isStoreOperationallyDisabled(store) && !seenIds.has(store.storeId)) {
      merged.push(store);
      seenIds.add(store.storeId);
    }
  });
  return merged;
};

/**
 * Applies the user's store display order (custom or alphabetical), with DISABLED stores last.
 */
const orderStoresForSelector = (
  stores: IStore[],
  displayOrder: TStoreDisplayOrder,
  customOrder: string[],
): IStore[] => {
  if (displayOrder === StoreDisplayOrderEnum.CUSTOM && customOrder.length > 0) {
    const ordered: IStore[] = [];
    const storeMap = new Map(stores.map((store: IStore) => [store.storeId, store]));

    customOrder.forEach((storeId: string) => {
      const store = storeMap.get(storeId);
      if (store) {
        ordered.push(store);
        storeMap.delete(storeId);
      }
    });

    storeMap.forEach((store: IStore) => ordered.push(store));
    return orderActiveStoresThenDisabled(ordered);
  }

  return orderActiveStoresThenDisabled(
    [...stores].sort((a: IStore, b: IStore) => a.name.localeCompare(b.name)),
  );
};

/**
 * Selector for retrieving stores by feature with ordering applied based on user preferences.
 * Supports both alphabetical and custom ordering.
 * DISABLED stores remain visible (for the selector) but are sorted to the end.
 */
export const
  selectStores = createSelector(
    selectLocalStoresByFeature,
    selectSelectedFeature,
    selectLocalStoreDisplayOrder,
    selectLocalCustomStoreOrder,
    selectAllUserStores,
    (
      stores: Record<string, IStore[]>,
      feature: IFeature,
      displayOrder: TStoreDisplayOrder,
      customOrder: string[],
      allUserStores: IStore[],
    ): IStore[] => {
      const featureStores = mergeFeatureStoresWithDisabled(
        stores[feature.feature] || <IStore[]>[],
        allUserStores,
      );
      return orderStoresForSelector(featureStores, displayOrder, customOrder);
    },
  );

/**
 * Every session store in the user's display order. Use this for the header picker so the
 * selected store does not change when navigating between features that only some stores can use.
 */
export const selectAllUserStoresOrdered = createSelector(
  selectAllUserStores,
  selectLocalStoreDisplayOrder,
  selectLocalCustomStoreOrder,
  (
    allUserStores: IStore[],
    displayOrder: TStoreDisplayOrder,
    customOrder: string[],
  ): IStore[] => orderStoresForSelector(allUserStores, displayOrder, customOrder),
);

/**
 * Selector for retrieving the currently selected store.
 *
 * @template MemoizedSelector
 * @returns {MemoizedSelector<object, IStore>} The selected store object.
 */
export const selectSelectedStore = createSelector(
  selectSharedUserStoreState,
  (state: IUserStoresState): IStore => state.selectedStore ?? <IStore>{},
);

export const selectSelectedStores = createSelector(
  selectSharedUserStoreState,
  (state: IUserStoresState): IStore[] => state.selectedMultipleStores || [] as IStore[],
);

export const selectSelectedStoresIds = createSelector(
  selectSharedUserStoreState,
  (state: IUserStoresState): string[] => state.selectedMultipleStores?.map((store: IStore) => store.storeId) ?? [],
);

/**
 * Raw session store list loaded at login / updated by session eviction (FE-95).
 * Prefer this over feature-scoped `selectStores` when filtering Firestore queries.
 */
export const selectSessionStores = createSelector(
  selectSharedUserStoreState,
  (state: IUserStoresState): IStore[] => state.stores ?? [],
);

export const selectSessionStoreIds = createSelector(
  selectSessionStores,
  (stores: IStore[]): string[] => stores.map((store: IStore) => store.storeId),
);

/**
 * Selector for retrieving the navigation object including the current selected page.
 *
 * @template MemoizedSelector
 * @returns {MemoizedSelector<object, { currentSelectedPage: INavigate }>} An object with the current navigation state.
 */
export const selectCurrentNavigationObj = createSelector(
  selectSharedUserStoreState,
  (state: IUserStoresState) => ({
    currentSelectedPage: state.navigation,
  }),
);

/**
 * Selector for retrieving the full navigation state.
 *
 * @template MemoizedSelector
 * @returns {MemoizedSelector<object, INavigate>} The full navigation object.
 */
export const selectCurrentNavigation = createSelector(
  selectSharedUserStoreState,
  (state: IUserStoresState): INavigate => state.navigation,
);

/**
 * Selector for retrieving the current navigation page.
 *
 * @template MemoizedSelector
 * @returns {MemoizedSelector<object, NavigationPageEnum>} The current page or a fallback to the login page.
 */
export const selectCurrentNavigationPage = createSelector(
  selectCurrentNavigation,
  (navigation: INavigate) => navigation?.currentPage || NavigationPageEnum.Login,
);
/**
 * Selector for retrieving the current saved changes state.
 *
 * @template MemoizedSelector
 * @returns {MemoizedSelector<boolean>} the value showing if all changes are saved.
 */
export const selectAreChangesSaved = createSelector(
  selectSharedUserStoreState,
  (state: IUserStoresState): boolean => state.areChangesSaved,
);

export const showLoadingIndicator = createSelector(
  selectSharedUserStoreState,
  (state: IUserStoresState): boolean => state.showLoadingIndicator,
);

/**
 * Selector for retrieving the navigation loading start time.
 *
 * @template MemoizedSelector
 * @returns {MemoizedSelector<object, number | null>} The navigation loading start timestamp.
 */
export const selectNavigationLoadingStartTime = createSelector(
  selectSharedUserStoreState,
  (state: IUserStoresState): number | null => state.navigationLoadingStartTime,
);
